import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	Input,
	NgZone,
	OnDestroy,
	signal,
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { animationFrames, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

@Component({
	selector: "clock",
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: "./clock.component.scss",
	template: `
		<div class="clock-container bg-white d-lg-inline-block px-3 py-2 rounded-3 text-center">
			<time class="d-inline-block text-start" [dateTime]="hours()+':'+minutes()+':'+seconds()">
				<span class="text-uppercase fw-bold text-xs">{{'auction.live.time' | translate}}</span><br>
				<strong class="fw-bold text-big text-lightblue text-xxl">
					<span class="pe-3">
						{{ hours() }}
					</span>
					:
					<span class="px-2">
						{{ minutes() }}
					</span>
					:
					<span class="ps-2">
						{{ seconds() }}
					</span>
				</strong>
			</time>
		</div>
	`,
	imports: [TranslatePipe],
})
export class ClockComponent implements OnDestroy {
	private zone = inject(NgZone);
	private sub?: Subscription;
	/** Delta time in milliseconds between local machine time and server-provided time */
	private deltaTime = 0;

	private now = signal(new Date());
	/** Delta time in milliseconds between local machine time and server-provided time */
	@Input({ required: true }) set timeOffset(value: number) {
		if (!isNaN(value)) {
			this.deltaTime = value;
			this.start();
		}
	}

	private pad = (num: number): string => num.toString().padStart(2, "0");

	hours = computed(() => this.pad(this.now().getHours()));
	minutes = computed(() => this.pad(this.now().getMinutes()));
	seconds = computed(() => this.pad(this.now().getSeconds()));

	private start() {
		this.stop();

		this.zone.runOutsideAngular(() => {
			this.sub = animationFrames().pipe(
				map(() => new Date(Date.now() + this.deltaTime)),
				distinctUntilChanged((prev, curr) => prev.getSeconds() === curr.getSeconds())
			).subscribe({
				next: updatedDate => {
					this.zone.run(() => {
						this.now.set(updatedDate);
					});
				}
			});
		});
	}

	private stop() {
		if (this.sub) {
			this.sub.unsubscribe();
			this.sub = undefined;
		}
	}

	ngOnDestroy() {
		this.stop();
	}
}